import { Button } from '@mui/material'
import { message } from 'antd'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import io from 'socket.io-client'
import store from 'store2'

import { JWT_TOKEN_NAME } from '../../../../../constants/auth-key'
import { axiosInstance } from '../../../../../hooks/useAxios'
import { useUser } from '../../../../../hooks/useUser'

interface Props {
  // เรียกหลัง backfill เสร็จ เพื่อให้ตารางโหลดยอดต้นทุนใหม่
  onCompleted?: () => void
  // เรียกทันทีหลังสั่งเริ่ม เพื่อให้แถบสถานะเปลี่ยนเป็น "กำลังเติม..." โดยไม่ต้องรอ poll รอบถัดไป
  onStarted?: () => void
  // ปิดปุ่มตอนมีงานวิ่งอยู่ (รู้จาก status endpoint แม้เพิ่ง refresh หน้า)
  disabled?: boolean
}

// เติมต้นทุนย้อนหลังให้ออเดอร์เก่าที่ยังไม่มีต้นทุน (ดึงจากสินค้าในระบบตาม SKU)
// ทำงานเบื้องหลัง — แจ้งผลผ่าน socket เหมือนปุ่มดึงคำสั่งซื้อย้อนหลัง
const CostBackfillButton = ({ onCompleted, onStarted, disabled }: Props) => {
  const { merchant } = useUser()
  const [loading, setLoading] = useState<boolean>(false)

  const username = merchant?.prefix

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_SERVICE
    if (!username || !socketUrl) {
      return
    }

    const jwtToken = store.get(JWT_TOKEN_NAME)
    const socket = io(socketUrl, {
      transports: ['websocket'],
      path: '/ws/',
      reconnection: true,
      query: { token: jwtToken ?? '' },
    })

    const subscribe = () => socket.emit('subscribe', { username })
    subscribe()
    socket.on('connect', subscribe)

    socket.on(
      'order:backfill-cost:completed',
      (data: { updated?: number; stillMissing?: number }) => {
        const stillMissing = data?.stillMissing ?? 0
        message.success(
          `เติมต้นทุนย้อนหลังเสร็จแล้ว ${data?.updated ?? 0} คำสั่งซื้อ` +
            (stillMissing
              ? ` — ยังไม่มีต้นทุนอีก ${stillMissing} คำสั่งซื้อ (SKU ยังไม่ผูกกับสินค้าในระบบ)`
              : ''),
        )
        onCompleted?.()
      },
    )
    socket.on('order:backfill-cost:failed', (data: { error?: string }) => {
      message.error(
        `เติมต้นทุนย้อนหลังล้มเหลว: ${data?.error ?? 'ไม่ทราบสาเหตุ'}`,
      )
    })

    return () => {
      socket.off()
      socket.disconnect()
    }
  }, [username, onCompleted])

  const handleBackfill = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.post<{
        status?: string
        scanned?: number
      }>('/tiktok/backfill-cost')
      const result = res.data
      if (result?.status === 'nothing_to_do') {
        message.info('ทุกคำสั่งซื้อมีต้นทุนครบแล้ว ไม่มีอะไรต้องเติม')
      } else {
        message.info(
          `กำลังเติมต้นทุนย้อนหลัง ${
            result?.scanned ?? 0
          } คำสั่งซื้อ จะแจ้งเตือนเมื่อเสร็จ`,
        )
      }
    } catch (e) {
      const err = e as AxiosError<{ message?: string }>
      if (err.response?.status === 409) {
        message.warning(
          'กำลังเติมต้นทุนย้อนหลังอยู่ กรุณารอให้รอบปัจจุบันเสร็จ',
        )
      } else {
        message.error('เริ่มเติมต้นทุนย้อนหลังไม่สำเร็จ กรุณาลองใหม่')
      }
    } finally {
      setLoading(false)
      // sync สถานะจริงจาก BE เสมอ ไม่ว่าจะเริ่มสำเร็จ ชนรอบเดิม (409) หรือพัง
      onStarted?.()
    }
  }

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handleBackfill}
      disabled={loading || disabled}
    >
      {loading ? 'กำลังเริ่ม...' : 'เติมต้นทุนย้อนหลัง'}
    </Button>
  )
}

export default CostBackfillButton
